/**
 * Inventory Management Hooks
 * Task 62: CRUD operations for inventory items
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { InventoryItem, InventoryTransaction, InventoryTransactionType, LowStockItem } from '@/types/inventory';
import { toast } from 'sonner';

// Fetch all inventory items
export function useInventoryItems() {
  return useQuery({
    queryKey: ['inventory_items'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as InventoryItem[];
    },
  });
}

// Fetch low stock items
export function useLowStockItems() {
  return useQuery({
    queryKey: ['low_stock_items'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('v_low_stock_items')
        .select('*')
        .eq('user_id', user.id)
        .order('shortage_quantity', { ascending: false });

      if (error) throw error;
      return data as LowStockItem[];
    },
  });
}

// Fetch inventory transactions for an item
export function useInventoryTransactions(itemId: string | null) {
  return useQuery({
    queryKey: ['inventory_transactions', itemId],
    queryFn: async () => {
      if (!itemId) return [];

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('inventory_transactions')
        .select('*')
        .eq('inventory_item_id', itemId)
        .eq('user_id', user.id)
        .order('transaction_date', { ascending: false });

      if (error) throw error;
      return data as InventoryTransaction[];
    },
    enabled: !!itemId,
  });
}

// Create inventory item
export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemData: Partial<InventoryItem>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: company } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!company) throw new Error('Company not found');

      const { data, error } = await supabase
        .from('inventory_items')
        .insert({
          ...itemData,
          company_id: company.id,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as InventoryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory_items'] });
      queryClient.invalidateQueries({ queryKey: ['low_stock_items'] });
      toast.success('Item adicionado ao estoque');
    },
    onError: (error: Error) => {
      toast.error('Erro ao adicionar item: ' + error.message);
    },
  });
}

// Update inventory item
export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<InventoryItem> }) => {
      const { data, error } = await supabase
        .from('inventory_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as InventoryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory_items'] });
      queryClient.invalidateQueries({ queryKey: ['low_stock_items'] });
      toast.success('Item atualizado');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar item: ' + error.message);
    },
  });
}

// Delete (deactivate) inventory item
export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('inventory_items')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory_items'] });
      queryClient.invalidateQueries({ queryKey: ['low_stock_items'] });
      toast.success('Item removido do estoque');
    },
    onError: (error: Error) => {
      toast.error('Erro ao remover item: ' + error.message);
    },
  });
}

// Create inventory transaction (stock movement)
export function useCreateInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionData: {
      inventory_item_id: string;
      transaction_type: InventoryTransactionType;
      quantity: number;
      unit_cost?: number;
      notes?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: company } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!company) throw new Error('Company not found');

      const { data, error } = await supabase
        .from('inventory_transactions')
        .insert({
          ...transactionData,
          company_id: company.id,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as InventoryTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory_items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory_transactions'] });
      queryClient.invalidateQueries({ queryKey: ['low_stock_items'] });
      toast.success('Movimentação de estoque registrada');
    },
    onError: (error: Error) => {
      toast.error('Erro ao registrar movimentação: ' + error.message);
    },
  });
}
